import { API } from '@/constant/constants'
import {
  Emitter,
  EtZone,
  Plant,
  SetupStation,
  UPLOAD_FILE_COMMON_RESPONSE,
  UPLOAD_FILE_RESPONSE,
} from '@/types'
import { AreaType } from '@/types/areaZone'
import { CommonData, DecoderTypeConfiguration } from '@/types/commonData'
import { FertigationSetup } from '@/types/fertigation'
import { Historical } from '@/types/historical'
import { HydraulicType } from '@/types/hydraulic'
import { SensorDetailImport } from '@/types/sensor'
import { Soil } from '@/types/soil'
import axiosClient from '../axiosClient'

export const comonDataApi = {
  uploadInitFile<T>(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.INIT.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<any>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  uploadHydraulicFile<T>(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
      .replace(':type', `${type}`)
      .replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<HydraulicType>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  getCommonTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadCommonFile<T>(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
      .replace(':type', `${type}`)
      .replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_COMMON_RESPONSE<T>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  uploadCommonFertigationFile<T>(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
      .replace(':type', `${type}`)
      .replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<FertigationSetup>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  uploadCommonNormalFile<T>(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
      .replace(':type', `${type}`)
      .replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<T>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  uploadStationCommonNormalFile<T>(
    interfaceId: number,
    type: number,
    data: FormData,
    page: number,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON_DATA_STATION.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
      .replace(':type', `${type}`)
      .replace(':page', `${page}`)
      .replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<T>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  uploadCommonSoilFile(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadCommonNormalFile<Soil>(
      interfaceId,
      type,
      data,
      isDeleted,
    )
  },

  uploadCommonEmitterFile(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadCommonNormalFile<Emitter>(
      interfaceId,
      type,
      data,
      isDeleted,
    )
  },

  uploadCommonAreaFile(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadCommonNormalFile<AreaType>(
      interfaceId,
      type,
      data,
      isDeleted,
    )
  },

  uploadCommonSensorFile(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadCommonNormalFile<SensorDetailImport>(
      interfaceId,
      type,
      data,
      isDeleted,
    )
  },

  uploadCommonEtZoneFile(
    interfaceId: number,
    type: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadCommonNormalFile<EtZone>(
      interfaceId,
      type,
      data,
      isDeleted,
    )
  },

  uploadCommonStationFile(
    interfaceId: number,
    type: number,
    data: FormData,
    page: number,
    isDeleted: boolean,
  ) {
    return comonDataApi.uploadStationCommonNormalFile<SetupStation>(
      interfaceId,
      type,
      data,
      page,
      isDeleted,
    )
  },

  deleteCommonFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.COMMON.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<CommonData>>(url)
  },

  deleteCommonType(interfaceId: number, type: number, page?: number) {
    const url =
      `/api/setup/${interfaceId}/common-data/delete-by-type?type=${type}` +
      (page ? `&page=${page}` : '')
    return axiosClient.post(url)
  },

  getPlantTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.PLANT.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadPlantFile(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.PLANT.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Plant>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deletePlantFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.PLANT.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Plant>>(url)
  },

  getEtReferenceTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.REFERENCE.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadEtReferenceFile(
    interfaceId: number,
    data: FormData,
    isDeleted: boolean,
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.REFERENCE.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Historical>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteEtReferenceFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.REFERENCE.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Historical>>(url)
  },

  getEmitterTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.EMITTER.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadEmitterFile(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.EMITTER.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Emitter>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteEmitterFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.EMITTER.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Emitter>>(url)
  },

  getSoilTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.SOIL.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadSoilFile(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.SOIL.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Soil>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteSoilFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.SOIL.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Soil>>(url)
  },

  getEtZoneTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.ETZONE.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadEtZoneFile(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.ETZONE.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<EtZone>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteEtZoneFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.ETZONE.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<EtZone>>(url)
  },

  getAreaTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.AREA.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },
  uploadAreaFile(interfaceId: number, data: FormData, isDeleted: boolean) {
    const url = API.CONFIG_SETUP.COMMON_DATA.AREA.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)
    return axiosClient.post<UPLOAD_FILE_RESPONSE<AreaType>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteAreaFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.AREA.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<AreaType>>(url)
  },

  getStationTemplate(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.STATION.TEMPLATE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.get(url, {
      responseType: 'blob',
    })
  },

  uploadStationFile(
    interfaceId: number,
    data: FormData,
    isDeleted: boolean,
    options?: { setProgress?: (number: number) => void },
  ) {
    const url = API.CONFIG_SETUP.COMMON_DATA.STATION.UPLOAD.replace(
      ':interfaceId',
      `${interfaceId}`,
    ).replace(':isDeleted', `${isDeleted}`)

    return axiosClient.post<UPLOAD_FILE_RESPONSE<SetupStation>>(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percent = (progressEvent.loaded / progressEvent.total) * 50
        options?.setProgress?.(percent)
      },
      onDownloadProgress: (progressEvent) => {
        const percent = 50 + (progressEvent.loaded / progressEvent.total) * 50
        options?.setProgress?.(percent)
      },
    })
  },

  deleteStationFile(interfaceId: number) {
    const url = API.CONFIG_SETUP.COMMON_DATA.STATION.DELETE.replace(
      ':interfaceId',
      `${interfaceId}`,
    )
    return axiosClient.post<UPLOAD_FILE_RESPONSE<Soil>>(url)
  },

  putDecoderType(payload: DecoderTypeConfiguration) {
    const url = '/api/controller/decoder-type-configuration'
    return axiosClient.put(url, payload)
  },
}

export default comonDataApi
